import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '@/components/Button/Button';
import { t } from '@/lib/i18n';
import styles from './EnhancePromptModal.module.css';

export interface EnhancePromptModalProps {
  /** Human-readable label of the field being enhanced (e.g. "Detailed instructions"). */
  fieldLabel: string;
  isPending: boolean;
  error: string | null;
  onCancel: () => void;
  /** Receives the user's free-text request — may be empty (then the backend
   * defaults to a generic "make this better" prompt). */
  onConfirm: (userRequest: string) => void;
}

/**
 * Modal that collects "what would you like enhanced?" before firing the
 * LLM call. Centered card over a backdrop, Escape dismisses, initial focus
 * on the textarea. Used by both the Edit form and the Add Member wizard's Brief
 * step so the enhance UX stays consistent across surfaces.
 */
export function EnhancePromptModal({
  fieldLabel,
  isPending,
  error,
  onCancel,
  onConfirm,
}: EnhancePromptModalProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputId = useId();
  const headingId = useId();
  const [value, setValue] = useState('');

  useEffect(() => {
    textareaRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isPending) onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isPending, onCancel]);

  return (
    <div className={styles.modalBackdrop}>
      <div role="dialog" aria-modal="true" aria-labelledby={headingId} className={styles.modal}>
        <h2 id={headingId} className={styles.modalHeading}>
          {t('Enhance {field} with AI', { field: fieldLabel })}
        </h2>
        <p className={styles.modalBody}>
          {t("Describe what you'd like changed. Leave blank to ask for a general improvement.")}
        </p>
        <label htmlFor={inputId} className={styles.label}>
          {t('What should change?')}
        </label>
        <textarea
          id={inputId}
          ref={textareaRef}
          rows={4}
          className={styles.textarea}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t('e.g. "Make this more concise" or "Add a step for citing sources".')}
          disabled={isPending}
          maxLength={1000}
        />
        {error ? (
          <div className={styles.errorMessage} role="alert">
            {error}
          </div>
        ) : null}
        <div className={styles.modalActions}>
          <Button variant="ghost" size="md" onClick={onCancel} disabled={isPending}>
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => onConfirm(value)}
            isLoading={isPending}
          >
            {isPending ? t('Enhancing…') : t('Enhance')}
          </Button>
        </div>
      </div>
    </div>
  );
}
